import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Users, CalendarDays, BookOpen, Loader2, Check, Swords } from "lucide-react";
import { API, apiFetch } from "@/lib/gp";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const fmtDate = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export default function TournamentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ opponent: "", score: "", result: "win" });

  const load = useCallback(() => {
    fetch(`${API}/tournaments/${id}`).then((r) => (r.ok ? r.json() : Promise.reject())).then(setData).catch(() => setData(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (data === false) return <div className="mx-auto max-w-3xl px-5 py-28 text-center"><h1 className="font-unbounded font-black text-3xl">Tournament not found</h1><Button onClick={() => navigate("/tournaments")} className="mt-6 rounded-full bg-gp-blue">Back</Button></div>;
  if (!data) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="animate-spin text-gp-blue" size={30} /></div>;

  const { tournament: t, registrations, results } = data;
  const isRegistered = user && registrations.some((r) => r.user_id === user.user_id);

  const register = async () => {
    if (!user) { toast.info("Sign in to register."); navigate("/login", { state: { from: `/tournaments/${id}` } }); return; }
    setBusy(true);
    try {
      const res = await apiFetch(`/tournaments/${id}/register`, { method: "POST" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || "Could not register. Try again.");
      toast.success("You're registered — good luck!");
      load();
    } catch (err) { toast.error(String(err.message || err)); } finally { setBusy(false); }
  };

  const submitResult = async (e) => {
    e.preventDefault();
    if (!user) { toast.info("Sign in to submit results."); return; }
    if (!isRegistered) { toast.info("Register for this tournament before submitting results."); return; }
    if (!form.opponent || !form.score) { toast.error("Add opponent and score."); return; }
    setBusy(true);
    try {
      const res = await apiFetch(`/tournaments/${id}/results`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || "Could not submit result.");
      toast.success("Result submitted for review.");
      setForm({ opponent: "", score: "", result: "win" });
      load();
    } catch (err) { toast.error(String(err.message || err)); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-5xl px-5 md:px-8 py-12">
      <span className="text-[11px] uppercase tracking-widest text-gp-blue">{t.game} · {t.format}</span>
      <h1 className="font-unbounded font-black tracking-tighter text-3xl sm:text-4xl uppercase mt-2">{t.name}</h1>
      <p className="text-white/70 mt-3 max-w-2xl">{t.description}</p>

      <div className="mt-6 grid sm:grid-cols-3 gap-3">
        <div className="glass rounded-xl p-4 flex items-center gap-3"><Trophy className="text-gp-gold" size={18} /><div><p className="text-xs text-white/50">Prize</p><p className="font-semibold">{t.prize || "Glory"}</p></div></div>
        <div className="glass rounded-xl p-4 flex items-center gap-3"><CalendarDays className="text-gp-blue" size={18} /><div><p className="text-xs text-white/50">Starts</p><p className="font-semibold text-sm">{fmtDate(t.start_at)}</p></div></div>
        <div className="glass rounded-xl p-4 flex items-center gap-3"><Users className="text-gp-green" size={18} /><div><p className="text-xs text-white/50">Players</p><p className="font-semibold">{registrations.length}/{t.max_players}</p></div></div>
      </div>

      <div className="mt-6">
        {isRegistered ? (
          <span className="inline-flex items-center gap-2 rounded-full bg-gp-green/15 text-gp-green px-5 py-3 font-semibold ring-1 ring-gp-green/30" data-testid="registered-badge"><Check size={17} /> You're registered</span>
        ) : (
          <Button data-testid="register-btn" onClick={register} disabled={busy} className="rounded-full px-8 py-6 bg-gp-blue hover:bg-gp-blue/90 font-bold text-base shadow-[0_0_22px_rgba(25,195,125,0.4)]">
            {busy ? <Loader2 className="animate-spin" size={18} /> : "Register now"}
          </Button>
        )}
      </div>

      {t.rules && (
        <div className="glass rounded-2xl p-6 mt-8">
          <h3 className="font-unbounded font-bold text-lg flex items-center gap-2"><BookOpen size={18} className="text-gp-blue" /> Rules</h3>
          <p className="text-white/70 mt-2 whitespace-pre-line">{t.rules}</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6 mt-8">
        {/* Submit result */}
        <div className="glass rounded-2xl p-6">
          <h3 className="font-unbounded font-bold text-lg flex items-center gap-2"><Swords size={18} className="text-gp-gold" /> Submit a match result</h3>
          {isRegistered ? (
          <form onSubmit={submitResult} className="mt-4 space-y-3" data-testid="result-form">
            <Input data-testid="result-opponent" value={form.opponent} onChange={(e) => setForm({ ...form, opponent: e.target.value })} placeholder="Opponent handle" className="bg-black/30 border-white/10 h-11 rounded-xl" />
            <Input data-testid="result-score" value={form.score} onChange={(e) => setForm({ ...form, score: e.target.value })} placeholder="Score (e.g. 2-1 or 145,320 chips)" className="bg-black/30 border-white/10 h-11 rounded-xl" />
            <div className="flex gap-2">
              {["win", "loss"].map((r) => (
                <button type="button" key={r} onClick={() => setForm({ ...form, result: r })} className={`flex-1 rounded-xl py-2.5 font-semibold capitalize transition-colors ${form.result === r ? (r === "win" ? "bg-gp-green/20 text-gp-green ring-1 ring-gp-green/40" : "bg-gp-red/20 text-gp-red ring-1 ring-gp-red/40") : "bg-white/5 text-white/60"}`}>{r}</button>
              ))}
            </div>
            <Button type="submit" data-testid="result-submit" disabled={busy} className="w-full rounded-full py-5 bg-gp-blue hover:bg-gp-blue/90 font-bold">Submit for review</Button>
          </form>
          ) : (
            <p className="text-white/55 text-sm mt-3" data-testid="result-register-first">Register for this tournament before submitting a result.</p>
          )}
        </div>

        {/* Recent results */}
        <div className="glass rounded-2xl p-6">
          <h3 className="font-unbounded font-bold text-lg">Recent results</h3>
          <div className="mt-4 space-y-2 max-h-72 overflow-y-auto gp-scroll" data-testid="results-list">
            {results.length === 0 && <p className="text-white/40 text-sm">No results submitted yet.</p>}
            {results.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-xl bg-black/25 px-4 py-3 text-sm">
                <span className="font-medium">{r.handle} <span className="text-white/40">vs</span> {r.opponent}</span>
                <span className="flex items-center gap-2">
                  <span className="text-white/60">{r.score}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.result === "win" ? "bg-gp-green/15 text-gp-green" : "bg-gp-red/15 text-gp-red"}`}>{r.result}</span>
                  {r.status === "verified" && <Check size={13} className="text-gp-blue" />}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
